import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, RefreshCw } from "lucide-react";
import { LogoStacked, LogoMark } from "../components/Logo";
import Seo from "../components/Seo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errMsg = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || "").join(" ");
  return "Request failed. Please try again.";
};

const inputCls =
  "w-full bg-coal border border-white/10 focus:border-electric-bright outline-none px-3 py-2.5 text-sm text-white placeholder:text-gray-600 transition-colors rounded-none";
const labelCls = "block text-[10px] font-bold uppercase tracking-[0.25em] text-gray-500 mb-1.5";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      onLogin(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5" data-testid="admin-login">
      <div className="w-full max-w-sm border border-white/10 bg-coal p-10">
        <div className="flex justify-center">
          <LogoStacked className="h-16 w-auto" />
        </div>
        <p className="mt-6 text-center text-[10px] font-bold uppercase tracking-[0.35em] text-gray-500">
          Admin Access
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className={labelCls} htmlFor="ad-email">Email</label>
            <input
              id="ad-email"
              data-testid="admin-email-input"
              type="email"
              required
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@themaplab.co.uk"
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="ad-pass">Password</label>
            <input
              id="ad-pass"
              data-testid="admin-password-input"
              type="password"
              required
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-button"
            className="w-full bg-electric hover:bg-electric-bright disabled:opacity-60 text-white text-xs font-bold uppercase tracking-[0.25em] py-3.5 transition-colors"
          >
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Enquiries({ bookings, onStatus, onDelete, onRefresh }) {
  if (bookings.length === 0) {
    return (
      <div className="border border-dashed border-white/15 p-14 text-center" data-testid="enquiries-empty">
        <p className="font-display text-xl uppercase tracking-tight text-white">No enquiries yet</p>
        <p className="mt-2 text-sm text-gray-500">Booking form submissions will appear here.</p>
      </div>
    );
  }
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid="enquiries-table">
      <div className="flex justify-end p-3 border-b border-white/10">
        <button onClick={onRefresh} data-testid="enquiries-refresh-button" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gray-400 hover:text-white transition-colors">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            <TableHead className="text-gray-500 uppercase text-[10px] tracking-[0.2em]">Received</TableHead>
            <TableHead className="text-gray-500 uppercase text-[10px] tracking-[0.2em]">Customer</TableHead>
            <TableHead className="text-gray-500 uppercase text-[10px] tracking-[0.2em]">Vehicle</TableHead>
            <TableHead className="text-gray-500 uppercase text-[10px] tracking-[0.2em]">Service</TableHead>
            <TableHead className="text-gray-500 uppercase text-[10px] tracking-[0.2em]">Preferred</TableHead>
            <TableHead className="text-gray-500 uppercase text-[10px] tracking-[0.2em]">Status</TableHead>
            <TableHead className="text-gray-500 uppercase text-[10px] tracking-[0.2em] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.map((b, i) => (
            <TableRow key={b.id} className="border-white/10 hover:bg-white/[0.02]" data-testid={`enquiry-row-${i}`}>
              <TableCell className="text-xs text-gray-400 whitespace-nowrap">
                {b.created_at ? new Date(b.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
              </TableCell>
              <TableCell>
                <p className="text-sm text-white font-medium">{b.name}</p>
                <p className="text-xs text-gray-500">{b.phone}</p>
                <p className="text-xs text-gray-500">{b.email}</p>
              </TableCell>
              <TableCell>
                <p className="text-sm text-white">{b.vehicle}</p>
                {b.fuel_type && <p className="text-xs text-gray-500">{b.fuel_type}</p>}
                {b.registration && <p className="text-xs text-gray-500 uppercase">{b.registration}</p>}
                {b.vin && <p className="text-xs text-gray-500 uppercase">VIN: {b.vin}</p>}
                {b.message && <p className="text-xs text-gray-600 mt-1 max-w-[220px]">{b.message}</p>}
              </TableCell>
              <TableCell className="text-sm text-gray-300 whitespace-nowrap">{b.service}</TableCell>
              <TableCell className="text-sm text-gray-300 whitespace-nowrap">{b.preferred_date || "—"}{b.preferred_time ? ` · ${b.preferred_time}` : ""}</TableCell>
              <TableCell>
                <select
                  value={b.status}
                  onChange={(e) => onStatus(b.id, e.target.value)}
                  data-testid={`enquiry-status-${i}`}
                  className="bg-coal border border-white/10 text-xs text-white px-2 py-1.5 outline-none focus:border-electric-bright"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="completed">Completed</option>
                </select>
              </TableCell>
              <TableCell className="text-right">
                <button
                  onClick={() => onDelete(b.id)}
                  data-testid={`enquiry-delete-${i}`}
                  className="text-gray-600 hover:text-red-400 transition-colors p-1.5"
                  aria-label="Delete enquiry"
                >
                  <Trash2 size={15} />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ContentEditor({ content, setContent, onSave, saving }) {
  const setContact = (k, v) => setContent((c) => ({ ...c, contact: { ...c.contact, [k]: v } }));
  const setHero = (k, v) => setContent((c) => ({ ...c, hero: { ...c.hero, [k]: v } }));
  const setService = (i, k, v) =>
    setContent((c) => {
      const services = [...c.services];
      services[i] = { ...services[i], [k]: v };
      return { ...c, services };
    });
  const addService = () =>
    setContent((c) => ({
      ...c,
      services: [
        ...c.services,
        { id: `service-${Date.now()}`, title: "New Service", tagline: "", description: "", points: [], image: "", price: "From £" },
      ],
    }));
  const removeService = (i) => setContent((c) => ({ ...c, services: c.services.filter((_, j) => j !== i) }));

  const setFaq = (i, k, v) =>
    setContent((c) => {
      const faqs = [...c.faqs];
      faqs[i] = { ...faqs[i], [k]: v };
      return { ...c, faqs };
    });
  const addFaq = () => setContent((c) => ({ ...c, faqs: [...c.faqs, { q: "New question", a: "" }] }));
  const removeFaq = (i) => setContent((c) => ({ ...c, faqs: c.faqs.filter((_, j) => j !== i) }));

  return (
    <div className="space-y-10" data-testid="content-editor">
      <div>
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight text-white mb-4">Contact Details</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Email</label><input className={inputCls} data-testid="edit-contact-email" value={content.contact.email} onChange={(e) => setContact("email", e.target.value)} /></div>
          <div><label className={labelCls}>Phone</label><input className={inputCls} data-testid="edit-contact-phone" value={content.contact.phone} onChange={(e) => setContact("phone", e.target.value)} placeholder="e.g. 01706 000000" /></div>
          <div><label className={labelCls}>WhatsApp number</label><input className={inputCls} data-testid="edit-contact-whatsapp" value={content.contact.whatsapp} onChange={(e) => setContact("whatsapp", e.target.value)} placeholder="e.g. 447123456789" /></div>
          <div><label className={labelCls}>Instagram handle</label><input className={inputCls} data-testid="edit-contact-instagram" value={content.contact.instagram} onChange={(e) => setContact("instagram", e.target.value)} /></div>
          <div><label className={labelCls}>Location</label><input className={inputCls} data-testid="edit-contact-location" value={content.contact.location} onChange={(e) => setContact("location", e.target.value)} /></div>
          <div><label className={labelCls}>Service area</label><input className={inputCls} data-testid="edit-contact-area" value={content.contact.service_area} onChange={(e) => setContact("service_area", e.target.value)} /></div>
        </div>
        <p className="mt-3 text-xs text-gray-600">Adding a WhatsApp number shows the WhatsApp enquiry button on the site.</p>
      </div>

      <div>
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight text-white mb-4">Hero</h3>
        <div className="space-y-4">
          <div><label className={labelCls}>Subtext</label><textarea rows={3} className={`${inputCls} resize-none`} data-testid="edit-hero-subtext" value={content.hero.subtext} onChange={(e) => setHero("subtext", e.target.value)} /></div>
          <div><label className={labelCls}>Background image URL</label><input className={inputCls} data-testid="edit-hero-image" value={content.hero.image} onChange={(e) => setHero("image", e.target.value)} /></div>
        </div>
      </div>

      <div>
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight text-white mb-4">About & Pricing Note</h3>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>About paragraphs (one per line)</label>
            <textarea
              rows={5}
              className={`${inputCls} resize-none`}
              data-testid="edit-about-paragraphs"
              value={content.about.paragraphs.join("\n")}
              onChange={(e) => setContent((c) => ({ ...c, about: { ...c.about, paragraphs: e.target.value.split("\n").filter(Boolean) } }))}
            />
          </div>
          <div>
            <label className={labelCls}>Pricing note</label>
            <textarea rows={2} className={`${inputCls} resize-none`} data-testid="edit-pricing-note" value={content.pricing_note} onChange={(e) => setContent((c) => ({ ...c, pricing_note: e.target.value }))} />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight text-white">Services & Prices</h3>
          <button onClick={addService} data-testid="add-service-button" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-electric-bright hover:text-white transition-colors">
            <Plus size={14} /> Add Service
          </button>
        </div>
        <div className="space-y-5">
          {content.services.map((s, i) => (
            <div key={s.id} className="border border-white/10 p-5 grid sm:grid-cols-2 gap-4" data-testid={`service-editor-${i}`}>
              <div><label className={labelCls}>Title</label><input className={inputCls} data-testid={`service-title-${i}`} value={s.title} onChange={(e) => setService(i, "title", e.target.value)} /></div>
              <div><label className={labelCls}>Price label</label><input className={inputCls} data-testid={`service-price-${i}`} value={s.price} onChange={(e) => setService(i, "price", e.target.value)} /></div>
              <div><label className={labelCls}>Tagline</label><input className={inputCls} value={s.tagline} onChange={(e) => setService(i, "tagline", e.target.value)} /></div>
              <div><label className={labelCls}>Image URL</label><input className={inputCls} value={s.image} onChange={(e) => setService(i, "image", e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea rows={3} className={`${inputCls} resize-none`} value={s.description} onChange={(e) => setService(i, "description", e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Key points (one per line)</label><textarea rows={3} className={`${inputCls} resize-none`} value={(s.points || []).join("\n")} onChange={(e) => setService(i, "points", e.target.value.split("\n").filter(Boolean))} /></div>
              <div className="sm:col-span-2 border-t border-white/10 pt-4"><p className="text-[10px] font-bold uppercase tracking-[0.25em] text-electric-bright">Detail Page (/services/{s.id})</p></div>
              <div className="sm:col-span-2"><label className={labelCls}>Detail intro paragraphs (one per line)</label><textarea rows={4} className={`${inputCls} resize-none`} data-testid={`service-intro-${i}`} value={(s.detail_intro || []).join("\n")} onChange={(e) => setService(i, "detail_intro", e.target.value.split("\n").filter(Boolean))} /></div>
              <div><label className={labelCls}>What's included (one per line)</label><textarea rows={4} className={`${inputCls} resize-none`} value={(s.includes || []).join("\n")} onChange={(e) => setService(i, "includes", e.target.value.split("\n").filter(Boolean))} /></div>
              <div><label className={labelCls}>Process steps (one per line)</label><textarea rows={4} className={`${inputCls} resize-none`} value={(s.process || []).join("\n")} onChange={(e) => setService(i, "process", e.target.value.split("\n").filter(Boolean))} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Detail note (shown highlighted)</label><textarea rows={2} className={`${inputCls} resize-none`} value={s.detail_note || ""} onChange={(e) => setService(i, "detail_note", e.target.value)} /></div>
              <div className="sm:col-span-2 flex justify-end">
                <button onClick={() => removeService(i)} data-testid={`service-remove-${i}`} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gray-600 hover:text-red-400 transition-colors">
                  <Trash2 size={13} /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight text-white">FAQ</h3>
          <button onClick={addFaq} data-testid="add-faq-button" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-electric-bright hover:text-white transition-colors">
            <Plus size={14} /> Add Question
          </button>
        </div>
        <div className="space-y-5">
          {content.faqs.map((f, i) => (
            <div key={i} className="border border-white/10 p-5 space-y-4" data-testid={`faq-editor-${i}`}>
              <div><label className={labelCls}>Question</label><input className={inputCls} data-testid={`faq-q-${i}`} value={f.q} onChange={(e) => setFaq(i, "q", e.target.value)} /></div>
              <div><label className={labelCls}>Answer</label><textarea rows={3} className={`${inputCls} resize-none`} data-testid={`faq-a-${i}`} value={f.a} onChange={(e) => setFaq(i, "a", e.target.value)} /></div>
              <div className="flex justify-end">
                <button onClick={() => removeFaq(i)} data-testid={`faq-remove-${i}`} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gray-600 hover:text-red-400 transition-colors">
                  <Trash2 size={13} /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="sticky bottom-0 bg-[#050505]/90 backdrop-blur border-t border-white/10 py-4 -mx-1 px-1">
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="save-content-button"
          className="bg-electric hover:bg-electric-bright disabled:opacity-60 text-white text-xs font-bold uppercase tracking-[0.25em] px-10 py-3.5 transition-colors shadow-[0_0_22px_rgba(6,69,243,0.35)]"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("enquiries");
  const [bookings, setBookings] = useState([]);
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadBookings = useCallback(async () => {
    const { data } = await axios.get(`${API}/bookings`, { withCredentials: true });
    setBookings(data);
  }, []);

  const loadContent = useCallback(async () => {
    const { data } = await axios.get(`${API}/content`);
    setContent({ faqs: [], ...data });
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) {
      loadBookings().catch(() => {});
      loadContent().catch(() => {});
    }
  }, [user, loadBookings, loadContent]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { status }, { withCredentials: true });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Status updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${API}/bookings/${id}`, { withCredentials: true });
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Enquiry deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, content, { withCredentials: true });
      toast.success("Site content saved — changes are live on the website.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-gray-500 text-sm uppercase tracking-[0.3em]">Checking session…</p>
      </div>
    );
  }
  if (user === false) return <><Seo title="Admin | TheMapLab" noindex /><Login onLogin={setUser} /></>;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <Seo title="Admin | TheMapLab" noindex />
      <header className="border-b border-white/10 bg-black/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <LogoMark className="h-6 w-auto" />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-500">Admin</span>
          </div>
          <div className="flex items-center gap-5">
            <a href="/" data-testid="admin-view-site-link" className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400 hover:text-white transition-colors">
              View Site
            </a>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gray-400 hover:text-red-400 transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-2 mb-10">
          {[
            ["enquiries", `Enquiries (${bookings.length})`],
            ["content", "Site Content"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`text-xs font-bold uppercase tracking-[0.2em] px-6 py-3 border transition-colors ${
                tab === key
                  ? "bg-electric border-electric text-white"
                  : "border-white/15 text-gray-400 hover:text-white hover:border-white/40"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <Enquiries bookings={bookings} onStatus={updateStatus} onDelete={deleteBooking} onRefresh={() => loadBookings().catch(() => {})} />
        )}
        {tab === "content" && content && (
          <ContentEditor content={content} setContent={setContent} onSave={saveContent} saving={saving} />
        )}
      </main>
    </div>
  );
}
